import React, { useState, useRef, useCallback, useMemo } from "react";
import { ConfigProvider } from "antd";
import Modal from "./components/Modal";
import "react-quill/dist/quill.snow.css";
import articleService from "../../../service/article.service";
import StatusHandler from "../../../components/articles/StatusHandler";
import { useArticleManagement } from "../../../hooks/useArticleManagement";
import { useArticleNavigation } from "../../../hooks/useArticleNavigation";
import ArticleSearch from "../../../components/articles/ArticleSearch";
import ArticleTable from "../../../components/articles/ArticleTable";
import Pagination from "../../../components/articles/Pagination";
import Swal from "sweetalert2";
import { antTheme, colors, tailwindClasses } from "../../../utils/theme";
import { 
  MdCheckCircle, 
  MdFileUpload, 
  MdImage, 
  MdAssignment,
  MdEdit 
} from "react-icons/md";
import useUserRole from "./../../../utils/useUserRole";

const ArticlePublier = () => {
  const fileInputRef = useRef(null);
  const [coverImage, setCoverImage] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(null);
  const [isLoading] = useState(false);
  const userRole = useUserRole();

  // Hook de navigation réutilisable
  const { navigateToDetails, navigateToEdit } = useArticleNavigation();

  // Utilisation du hook partagé avec navigation personnalisée
  const {
    error,
    searchTerm,
    currentItems,
    handleSearch,
    getArticleTitle,
    getArticleAuthor,
    getArticleDate,
    fetchArticles,
    currentPage,
    handlePageChange,
    itemsPerPage,
    handleItemsPerPageChange,
    indexOfFirstItem,
    indexOfLastItem,
    totalItems,
    totalPages,
    isModalOpen,
    setIsModalOpen,
    modalTitle,
    setModalTitle,
    modalSize,
    setModalSize,
    selectedArticle,
    isSubmitting,
    setIsSubmitting,
    submitError,
    setSubmitError,
    submitSuccess,
    setSubmitSuccess,
    modalContent,
  } = useArticleManagement(() => articleService.getAllArticlePublie(), null, {
    enableModal: true,
    enableEdit: false,
    customNavigationHandler: navigateToDetails,
  });

  // Callback de mise à jour de statut
  const handleStatusUpdate = useCallback(async ({ article, newStatus, comment }) => {
    console.log("🔄 Callback mise à jour statut article publié:", {
      articleId: article.id_art,
      fromStatus: 5, // Publié
      toStatus: newStatus,
      commentLength: comment.length,
      user: "HiQaody",
      timestamp: "2025-08-31 07:48:34",
    });

    try {
      await fetchArticles();
      setIsModalOpen(false);
      
      console.log("✅ Articles publiés rechargés après changement statut:", {
        newTotalItems: totalItems,
        user: "HiQaody",
        timestamp: "2025-08-31 07:48:34",
      });
    } catch (error) {
      console.error("❌ Erreur lors du rechargement des articles publiés:", {
        error: error.message,
        user: "HiQaody",
        timestamp: "2025-08-31 07:48:34",
      });
    }
  }, [fetchArticles, setIsModalOpen, totalItems]);

  // Configuration personnalisée pour ArticlePublier
  const customStatusFilter = useCallback((status) => {
    // Exclure les statuts "publié" et "en attente" (car on est déjà publié)
    // Permettre : 2 (à remanier), 3 (publiable), 4 (en révision), 7 (refusé)
    return [2, 3, 4, 7].includes(status.id_stat);
  }, []);

  // Règles d'avertissement spécifiques pour ArticlePublier
  const warningRules = {
    unpublishToRevision: {
      condition: (orig, new_stat) => 
        (orig === '5' || orig === 5) && (new_stat === '2' || new_stat === 2),
      title: '⚠️ Attention: Dépublication',
      description: 'Vous allez retirer cet article de la publication et le remettre en révision.',
      config: {
        title: '📝 Retour en révision',
        text: 'Vous êtes sur le point de retirer cet article de la publication et le remettre en révision. Il ne sera plus visible publiquement.',
        icon: 'warning',
        confirmButtonText: 'Confirmer la dépublication'
      }
    },
    unpublishToPublishable: {
      condition: (orig, new_stat) => 
        (orig === '5' || orig === 5) && (new_stat === '3' || new_stat === 3),
      title: '🔄 Retour en publiable',
      description: 'Vous allez retirer cet article de la publication et le marquer comme publiable.',
      config: {
        title: '🚀 Retour en publiable',
        text: 'Vous retirez cet article de la publication pour le marquer comme publiable. Il devra être republié.',
        icon: 'info',
        confirmButtonText: 'Confirmer le retour en publiable'
      }
    },
    unpublishToRejected: {
      condition: (orig, new_stat) => 
        (orig === '5' || orig === 5) && (new_stat === '7' || new_stat === 7),
      title: '❌ Dépublication et refus',
      description: 'Vous allez retirer cet article de la publication et le refuser.',
      config: {
        title: '❌ Refus et dépublication',
        text: 'Vous retirez cet article de la publication et le refusez définitivement. Cette action nécessite une justification solide.',
        icon: 'error',
        confirmButtonText: 'Confirmer le refus'
      }
    },
    toRevisionMode: {
      condition: (orig, new_stat) => 
        (orig === '5' || orig === 5) && (new_stat === '4' || new_stat === 4),
      title: '🔍 Mode révision',
      description: 'Vous placez cet article en mode révision tout en maintenant sa publication.',
      config: {
        title: '🔍 Passage en mode révision',
        text: 'Vous placez cet article en mode révision. Il restera publié mais sera marqué comme en cours de révision.',
        icon: 'info',
        confirmButtonText: 'Confirmer le mode révision'
      }
    }
  };

  // Suggestions de commentaires pour ArticlePublier
  const suggestionComments = [
    "Retrait temporaire pour corrections mineures",
    "Mise à jour nécessaire suite à nouvelles informations",
    "Révision demandée par l'équipe éditoriale",
    "Corrections typographiques et mise en forme",
    "Actualisation des données et références",
    "Amélioration de la structure du document",
    "Erreur détectée nécessitant une correction",
    "Demande de l'auteur pour révision",
    "Non-conformité détectée après publication",
    "Mise à jour réglementaire requise"
  ];

  // Formater une date ISO
  const formatDate = useCallback((dateString) => {
    if (!dateString) return "Non spécifiée";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);

  // Gérer le changement d'image de couverture
  const handleCoverImageChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      console.log("🖼️ Changement image de couverture:", {
        fileName: file.name,
        fileSize: file.size,
        user: "HiQaody",
        timestamp: "2025-08-31 07:48:34",
      });

      setCoverImage(file);
      const objectUrl = URL.createObjectURL(file);
      setCoverImagePreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
  }, []);

  const triggerFileInput = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  // Publication de l'article avec promesses
  const handleSubmitPublication = useCallback(() => {
    if (!coverImage) {
      setSubmitError("Veuillez sélectionner une image de couverture.");
      return;
    }

    console.log("🚀 Soumission republication article:", {
      articleId: selectedArticle.id_art,
      coverImageName: coverImage.name,
      user: "HiQaody",
      timestamp: "2025-08-31 07:48:34",
    });

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    const formData = new FormData();
    formData.append("photo_art", coverImage);

    articleService
      .publishArticle(selectedArticle.id_art, formData)
      .then(() => {
        setSubmitSuccess(true);
        setIsModalOpen(false);

        console.log("✅ Article republié avec succès:", {
          articleId: selectedArticle.id_art,
          user: "HiQaody",
          timestamp: "2025-08-31 07:48:34",
        });

        Swal.fire({
          icon: "success",
          title: "Republié !",
          text: "L'article a été republié avec succès",
          confirmButtonColor: colors.primary,
          timer: 3000,
          timerProgressBar: true,
        });

        fetchArticles();
        window.location.reload();
      })
      .catch((error) => {
        const errorMsg =
          error.response?.data?.message ||
          "Une erreur s'est produite lors de la republication.";
        
        console.error("❌ Erreur lors de la republication:", {
          error: errorMsg,
          articleId: selectedArticle.id_art,
          user: "HiQaody",
          timestamp: "2025-08-31 07:48:34",
        });

        setSubmitError(errorMsg);

        Swal.fire({
          icon: "error",
          title: "Erreur de republication",
          text: errorMsg,
          confirmButtonColor: colors.danger,
          confirmButtonText: "OK",
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [
    coverImage,
    fetchArticles,
    selectedArticle,
    setIsModalOpen,
    setIsSubmitting,
    setSubmitError,
    setSubmitSuccess,
  ]);

  // Mémoriser le contenu modal de publication
  const publicationModalContent = useMemo(() => {
    if (!selectedArticle || modalTitle !== "Publier cet article") return null;

    return (
      <div className="p-6">
        <div className="mb-4">
          <h3 className="mb-2 text-lg font-medium text-gray-900">
            {getArticleTitle(selectedArticle)}
          </h3>
          <p className="text-sm text-gray-600">
            Vous êtes sur le point de republier cet article. Veuillez ajouter une
            image de couverture mise à jour.
          </p>
        </div>
        <div className="mt-6 space-y-4">
          <div>
            <div className="flex items-center justify-between">
              <label
                htmlFor="cover-image-upload"
                className="block text-sm font-medium text-gray-700"
              >
                Photo de couverture
              </label>
              <span className="text-xs text-gray-500">
                Formats acceptés: JPG, JPEG, PNG
              </span>
            </div>
            <input
              id="cover-image-upload"
              type="file"
              ref={fileInputRef}
              className="hidden"
              onChange={handleCoverImageChange}
              accept="image/jpeg,image/png,image/jpg"
            />
            {coverImagePreview ? (
              <div className="mt-2">
                <div className="relative overflow-hidden rounded-lg">
                  <img
                    src={coverImagePreview}
                    alt="Aperçu de l'image de couverture"
                    className="h-64 w-full object-cover"
                  />
                  <button
                    onClick={triggerFileInput}
                    className="absolute bottom-2 right-2 rounded-full bg-gray-800 bg-opacity-70 p-2 text-white hover:bg-opacity-90"
                    title="Changer l'image"
                  >
                    <MdFileUpload className="h-5 w-5" />
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {coverImage?.name} (
                  {coverImage
                    ? (coverImage.size / 1024 / 1024).toFixed(2) + " MB"
                    : ""}
                  )
                </p>
              </div>
            ) : (
              <div
                onClick={triggerFileInput}
                className="mt-2 flex h-64 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100"
              >
                <div className="flex flex-col items-center justify-center pb-6 pt-5">
                  <MdImage className="mb-3 h-12 w-12 text-gray-400" />
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">
                      Cliquez pour téléverser
                    </span>{" "}
                    ou glissez-déposez
                  </p>
                  <p className="text-xs text-gray-500">
                    Image de couverture pour l'article
                  </p>
                </div>
              </div>
            )}
          </div>
          {submitError && (
            <div className={tailwindClasses.alertError}>
              <p>{submitError}</p>
            </div>
          )}
          {submitSuccess && (
            <div className={tailwindClasses.alertSuccess}>
              <div className="flex items-center">
                <MdCheckCircle className="mr-2 h-5 w-5" />
                <p>Article republié avec succès!</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }, [
    selectedArticle,
    modalTitle,
    getArticleTitle,
    coverImage,
    coverImagePreview,
    handleCoverImageChange,
    triggerFileInput,
    submitError,
    submitSuccess,
  ]);

  // État de chargement
  if (isLoading) {
    console.log("⏳ Chargement des articles publiés:", {
      user: "HiQaody",
      timestamp: "2025-08-31 07:48:34",
    });

    return (
      <div className="flex h-96 items-center justify-center">
        <div
          className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2"
          style={{ borderColor: colors.primary }}
        ></div>
      </div>
    );
  }

  // État d'erreur
  if (error) {
    console.error("❌ Erreur lors du chargement des articles publiés:", {
      error,
      user: "HiQaody",
      timestamp: "2025-08-31 07:48:34",
    });

    return (
      <div className={tailwindClasses.alertError}>
        <p>{error}</p>
        <button
          onClick={fetchArticles}
          className="mt-4 rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-700"
        >
          Réessayer
        </button>
      </div>
    );
  }

  console.log("🖼️ Rendu ArticlePublier:", {
    totalItems,
    currentPage,
    userRole,
    hasItems: currentItems.length > 0,
    user: "HiQaody",
    timestamp: "2025-08-31 07:48:34",
  });

  return (
    <ConfigProvider theme={antTheme}>
      <div className="container mx-auto px-4 py-6">
        <ArticleSearch
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          title="Articles publiés"
          subtitle={`${totalItems} article${totalItems !== 1 ? "s" : ""} publié${totalItems !== 1 ? "s" : ""}`}
          primaryColor={colors.primary}
        />

        <StatusHandler
          onStatusUpdate={handleStatusUpdate}
          showConfirmation={true}
          customStatusFilter={customStatusFilter}
          warningRules={warningRules}
          minimumCommentLength={20} // Plus strict pour articles publiés
          suggestionComments={suggestionComments}
        >
          {({ 
            initializeStatusAttribution, 
            selectedArticle: statusSelectedArticle,
            StatusSelector,
            CommentInput, 
            StatusMessages,
            handleSubmitStatus,
            isValid,
            isSubmitting: statusIsSubmitting 
          }) => (
            <>
              <div className={`mb-6 overflow-hidden`}>
                <ArticleTable
                  currentItems={currentItems}
                  getArticleTitle={getArticleTitle}
                  getArticleAuthor={getArticleAuthor}
                  getArticleDate={getArticleDate}
                  handleActionPrimary={
                    userRole === "Administrateur" ? undefined : (article) => {
                      console.log("✏️ Édition article publié:", {
                        articleId: article.id_art,
                        title: getArticleTitle(article),
                        user: "HiQaody",
                        timestamp: "2025-08-31 07:48:34",
                      });
                      navigateToEdit(article);
                    }
                  }
                  handleViewDetails={navigateToDetails}
                  showEditButton={false}
                  showPrimaryButton={userRole !== "Administrateur"}
                  actionPrimaryText={
                    userRole === "Administrateur" ? null : "Modifier"
                  }
                  actionPrimaryIcon={MdEdit}
                  showStatusButton={userRole !== "Administrateur"}
                  handleStatusArticle={
                    userRole === "Administrateur" ? undefined : (article) => {
                      console.log("📊 Attribution statut article publié:", {
                        articleId: article.id_art,
                        currentStatus: article.id_stat, // Devrait être 5 (publié)
                        user: "HiQaody",
                        timestamp: "2025-08-31 07:48:34",
                      });
                      initializeStatusAttribution(article);
                      setModalTitle("Attribuer des status");
                      setModalSize("2xl");
                      setIsModalOpen(true);
                    }
                  }
                  primaryColor={colors.primary}
                  dateColumnLabel="Date de publication"
                />

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  handlePageChange={handlePageChange}
                  itemsPerPage={itemsPerPage}
                  handleItemsPerPageChange={handleItemsPerPageChange}
                  indexOfFirstItem={indexOfFirstItem}
                  indexOfLastItem={indexOfLastItem}
                  totalItems={totalItems}
                  primaryColor={colors.primary}
                />
              </div>

              {/* Modal avec StatusHandler */}
              <Modal
                isOpen={isModalOpen}
                onClose={() => {
                  console.log("🔄 Fermeture modal articles publiés:", {
                    modalTitle,
                    user: "HiQaody",
                    timestamp: "2025-08-31 07:48:34",
                  });
                  setIsModalOpen(false);
                }}
                title={modalTitle}
                width={modalSize}
              >
                {modalTitle === "Attribuer des status" && statusSelectedArticle ? (
                  <div className="bg-white">
                    {/* En-tête de la modal avec informations de l'article */}
                    <div className="border-b border-gray-200 px-6 py-4">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100">
                            <svg
                              className="h-5 w-5 text-emerald-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="truncate text-lg font-semibold text-gray-900">
                            {getArticleTitle(statusSelectedArticle)}
                          </h3>
                          <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                            <span>Par {getArticleAuthor(statusSelectedArticle)}</span>
                            <span>•</span>
                            <span>
                              Publié le {formatDate(statusSelectedArticle?.date_pub_art || statusSelectedArticle?.date_soum_art)}
                            </span>
                            <span>•</span>
                            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                              Article publié
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Information contextuelle */}
                    <div className="border-b border-gray-100 bg-emerald-50 px-6 py-3">
                      <div className="flex items-center space-x-2 text-sm text-emerald-800">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-5 5-5-5h5v-5a7.5 7.5 0 01-7.5-7.5h5a2.5 2.5 0 100-5z" />
                        </svg>
                        <span className="font-medium">
                          Article actuellement en ligne et accessible au public
                        </span>
                      </div>
                    </div>

                    {/* Corps de la modal */}
                    <div className="space-y-6 px-6 py-6">
                      {/* Information contextuelle pour articles publiés */}
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <svg
                              className="mt-0.5 h-5 w-5 text-amber-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.348 16.5c-.77.833.192 2.5 1.732 2.5z"
                              />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <h5 className="text-sm font-medium text-amber-900">
                              ⚠️ Attention: Modification d'article publié
                            </h5>
                            <p className="mt-1 text-sm text-amber-700">
                              Cet article est actuellement publié et visible par le public. 
                              Tout changement de statut le retirera de la publication. 
                              Cette action nécessite une justification détaillée.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Sélection du nouveau statut */}
                      {StatusSelector}

                      {/* Section commentaire avec exigence renforcée */}
                      <div className="rounded-lg border border-red-200 bg-red-50 p-3 mb-4">
                        <div className="flex items-center space-x-2 text-sm text-red-800">
                          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          <span className="font-medium">
                            Justification obligatoire (minimum 20 caractères)
                          </span>
                        </div>
                      </div>

                      {CommentInput}

                      {/* Messages d'erreur et de succès */}
                      {StatusMessages}
                    </div>

                    {/* Footer avec boutons */}
                    <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">
                      <button
                        className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                        onClick={() => {
                          console.log("❌ Annulation changement statut article publié:", {
                            user: "HiQaody",
                            timestamp: "2025-08-31 07:48:34",
                          });
                          setIsModalOpen(false);
                        }}
                        disabled={statusIsSubmitting}
                      >
                        Annuler
                      </button>
                      
                      <button
                        className={`rounded-lg px-6 py-2 text-sm font-medium text-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                          isValid && !statusIsSubmitting
                            ? "bg-[#57A863] hover:bg-[#4a9154] focus:ring-[#57A863] shadow-lg hover:shadow-xl"
                            : "cursor-not-allowed bg-gray-400 focus:ring-gray-400"
                        }`}
                        onClick={() => {
                          console.log("🚀 Soumission changement statut article publié:", {
                            articleId: statusSelectedArticle.id_art,
                            newStatus: statusSelectedArticle.id_stat,
                            isValid,
                            user: "HiQaody",
                            timestamp: "2025-08-31 07:48:34",
                          });
                          handleSubmitStatus();
                        }}
                        disabled={!isValid || statusIsSubmitting}
                      >
                        {statusIsSubmitting ? (
                          <span className="flex items-center">
                            <svg
                              className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Modification en cours...
                          </span>
                        ) : (
                          <span className="flex items-center">
                            <MdAssignment className="mr-2 h-4 w-4" />
                            Modifier le statut
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                ) : modalTitle === "Publier cet article" ? (
                  publicationModalContent
                ) : (
                  modalContent
                )}

                {/* Footer pour modal de republication */}
                {modalTitle === "Publier cet article" && (
                  <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">
                    <button
                      className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                      onClick={() => {
                        console.log("❌ Annulation republication:", {
                          articleId: selectedArticle?.id_art,
                          user: "HiQaody",
                          timestamp: "2025-08-31 07:48:34",
                        });
                        setIsModalOpen(false);
                      }}
                      disabled={isSubmitting}
                    >
                      Annuler
                    </button>
                    
                    <button
                      className={`rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm ${
                        isSubmitting || !coverImage
                          ? "cursor-not-allowed bg-opacity-60"
                          : "hover:opacity-90 focus:ring-offset"
                      }`}
                      onClick={handleSubmitPublication}
                      disabled={isSubmitting || !coverImage}
                      style={{
                        backgroundColor:
                          isSubmitting || !coverImage
                            ? `${colors.primary}80`
                            : colors.primary,
                      }}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center">
                          <svg
                            className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Republication en cours...
                        </span>
                      ) : (
                        <span className="flex items-center">
                          <MdEdit className="mr-2 h-4 w-4" />
                          Republier l'article
                        </span>
                      )}
                    </button>
                  </div>
                )}
              </Modal>
            </>
          )}
        </StatusHandler>
      </div>
    </ConfigProvider>
  );
};

export default ArticlePublier;